import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { EnvironmentOverrideBar } from '@openx/components-core/lib/EnvironmentOverrideBar/EnvironmentOverrideBar.tsx';
import { OutsideLink } from '@openx/components-core/lib/Link/Link.tsx';
import { Logo } from '@openx/components-core/lib/Logo/Logo.tsx';
import { OpenXSelectLogo } from '@openx/components-core/lib/Logo/OpenXSelectLogo.tsx';

import type { JSX } from 'react';

const currentYear = new Date().getFullYear();

const styles: { [key: string]: SxProps<Theme> } = {
  centeredContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
  },
  copyright: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '30px 50px',
  },
  links: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  promoContainer: {
    background: theme => theme.palette.secondary.main,
    color: theme => theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 50px',
  },
  rootContainer: {
    backgroundColor: theme => theme.palette.background.paper,
    flex: 1,
    minHeight: 0,
  },
};

const EXTERNAL_LINKS: { title: string; link: string }[] = [
  {
    link: 'https://www.openx.com/legal/terms-and-conditions',
    title: 'Terms and Conditions',
  },
  {
    link: 'https://www.openx.com/legal/privacypolicy',
    title: 'Privacy Policy',
  },
  {
    link: 'https://www.openx.com/about',
    title: 'About',
  },
  {
    link: 'https://www.openx.com/support',
    title: 'Help',
  },
];

export function SsoLayout({ children }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <EnvironmentOverrideBar />
      <Grid
        container
        direction="row"
        sx={{
          justifyContent: 'center',
          alignItems: 'stretch',
          ...styles.rootContainer,
        }}
      >
        <Grid size={4} sx={styles.formContainer}>
          <Box>
            <Logo data-test="logo" />
          </Box>
          <Box sx={styles.centeredContainer}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: '24px' }}>
              <OpenXSelectLogo />
              <Typography variant="h1" sx={{ color: theme => theme.palette.secondary.main }}>
                OpenXSelect
              </Typography>
            </Box>
            {children}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {EXTERNAL_LINKS.map(({ title, link }, index) => (
              <OutsideLink
                key={index}
                to={link}
                openInNewTab={true}
                sx={{
                  padding: '4px',
                  fontSize: 12,
                  fontWeight: 'fontWeightBold',
                  textTransform: 'uppercase',
                  color: theme => theme.palette.text.secondary,
                }}
              >
                {title}
              </OutsideLink>
            ))}
          </Box>
        </Grid>
        <Grid size={8} sx={styles.promoContainer}>
          <Box
            data-test="advert-text"
            sx={{ display: 'flex', flex: 1, flexDirection: 'column', justifyContent: 'center', gap: '24px' }}
          >
            <Typography variant="display" component="h1">
              Reach Your Target Audience
            </Typography>
            <Typography variant="displaySubtitle" component="h2">
              Welcome to OpenXSelect™, where you can manage media buys to reach your target audience and maximize your
              ROI
            </Typography>
          </Box>
          <Box sx={styles.copyright}>
            <Typography variant="body2">Copyright © {currentYear} OpenX. All rights reserved.</Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
